import React, { useEffect, useMemo, useState } from 'react';
import { useSurvey } from './Context';
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList
} from 'recharts';

// Custom tick component for multi-line labels
const CustomYAxisTick = ({ x, y, payload, isMobile }) => {
    const text = String(payload.value || '');

    if (isMobile) {
        // On mobile, render the label ABOVE the bar row, left-aligned
        const maxWidth = 520; // wide enough; overflow hidden below will prevent spill
        const fontSize = 14;
        const lineHeight = 18;

        return (
            <g transform={`translate(${x},${y})`}>
                <foreignObject x={8} y={-38} width={maxWidth} height={36}>
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'flex-end',
                            justifyContent: 'flex-start',
                            fontSize: `${fontSize}px`,
                            fontWeight: 'bold',
                            color: '#666',
                            lineHeight: `${lineHeight}px`,
                            textAlign: 'left',
                            wordWrap: 'break-word',
                            overflow: 'hidden'
                        }}
                    >
                        {text}
                    </div>
                </foreignObject>
            </g>
        );
    }

    // Desktop: keep labels to the LEFT of bars
    const maxWidth = 140;
    const fontSize = 12;
    const lineHeight = 16;

    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-maxWidth} y={-20} width={maxWidth} height={40}>
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        fontSize: `${fontSize}px`,
                        fontWeight: 'bold',
                        color: '#666',
                        lineHeight: `${lineHeight}px`,
                        textAlign: 'right',
                        wordWrap: 'break-word',
                        overflow: 'hidden'
                    }}
                >
                    {text}
                </div>
            </foreignObject>
        </g>
    );
};

// Custom label component for better handling of small values
const CustomLabel = ({ x, y, width, height, value, index, dataKey }) => {
    // Don't show labels for zero values
    if (!value || value === 0) return null;

    const barWidth = width || 0;

    // Always show labels inside the bar, but adjust font size and color for small values
    const isSmallValue = value < 5;
    const fontSize = isSmallValue ? 9 : 13;
    const fontWeight = isSmallValue ? 600 : 700;

    // For very small values, use white text with a subtle shadow for better contrast
    const textColor = '#fff';

    return (
        <g>
            {/* Add a subtle shadow for small values to improve readability */}
            {isSmallValue && (
                <text
                    x={x + barWidth / 2 + 1}
                    y={y + height / 2 + 1}
                    textAnchor="middle"
                    fill="rgba(0,0,0,0.3)"
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    dominantBaseline="middle"
                >
                    {value}%
                </text>
            )}
            <text
                x={x + barWidth / 2}
                y={y + height / 2}
                textAnchor="middle"
                fill={textColor}
                fontSize={fontSize}
                fontWeight={fontWeight}
                dominantBaseline="middle"
            >
                {value}%
            </text>
        </g>
    );
};

const COLORS = [
  '#e74c3c', // Red
  '#34495e', // Navy
  '#27ae60', // Green
  '#6c3483', // Dark Purple
  '#0097e6', // Azure
  '#117864', // Dark Teal
  '#fdcb6e', // Yellow
  '#d35400', // Pumpkin
  '#c0392b', // Dark Red
  '#8e44ad', // Purple
  '#6c3483', // Dark Purple
  '#2980b9', // Blue
  '#1f618d', // Dark Blue
  '#117864', // Dark Teal
  '#229954', // Dark Green
  '#f39c12', // Orange
  '#16a085', // Teal
  '#b9770e', // Dark Orange
  '#ba4a00', // Dark Pumpkin
  '#2c3e50', // Dark Navy
  '#7f8c8d', // Gray
  '#566573', // Dark Gray
  '#273746', // Very Dark Gray
  '#e67e22', // Carrot
  '#ca6f1e', // Dark Carrot
  '#e84393', // Pink
  '#ad1457', // Dark Pink
  '#00b894', // Light Teal
  '#00897b', // Deep Teal
  '#00cec9', // Cyan
  '#0984e3', // Bright Blue
  '#636e72', // Slate
  '#2d3436', // Charcoal
  '#e17055', // Coral
  '#d63031', // Crimson
  '#6ab04c', // Light Green
  '#218c5a', // Forest Green
  '#4834d4', // Indigo
  '#130f40', // Deep Indigo
  '#be2edd', // Violet
  '#6c5ce7', // Blue Violet
  '#30336b', // Midnight Blue
  '#b2bec3', // Silver
  '#636e72', // Slate Gray
  '#00b894', // Jade
  '#00b8d4', // Sky Blue
  '#0097e6', // Azure
  '#e84118', // Tomato
  '#c23616', // Dark Tomato
  '#fbc531', // Gold
  '#4cd137', // Spring Green
  '#487eb0'  // Cobalt
];

/**
 * Rounds percentages using largest remainder method to ensure they always sum to 100%
 * This prevents stacked bars from not filling the full width due to rounding errors
 *
 * @param {Array} responses - Array of response objects with percentage values
 * @returns {Array} - Array with rounded percentages that sum to 100%
 *
 * To revert: Replace calls to this function with: Math.round(response.percentage)
 */
function roundPercentagesToTotal(responses) {
    const total = responses.reduce((sum, response) => sum + response.percentage, 0);

    // If responses are empty or total is 0, return as-is
    if (responses.length === 0 || total === 0) {
        return responses.map(response => ({
            ...response,
            percentage: Math.round(response.percentage)
        }));
    }

    // Step 1: Round all down to integers and calculate remainders
    const processedResponses = responses.map(response => {
        const scaledPercentage = (response.percentage / total) * 100; // Normalize to 100%
        const floorValue = Math.floor(scaledPercentage);
        const remainder = scaledPercentage - floorValue;

        return {
            ...response,
            floorValue,
            remainder,
            originalPercentage: response.percentage
        };
    });

    // Step 2: Calculate how many points to distribute
    const currentSum = processedResponses.reduce((sum, item) => sum + item.floorValue, 0);
    const pointsToDistribute = 100 - currentSum;

    // Step 3: Sort by remainder (largest first) and distribute points
    const sortedByRemainder = [...processedResponses].sort((a, b) => b.remainder - a.remainder);

    // Step 4: Add 1 to the top N values
    for (let i = 0; i < Math.min(pointsToDistribute, sortedByRemainder.length); i++) {
        sortedByRemainder[i].floorValue += 1;
    }

    // Step 5: Return in original order with rounded percentages
    return responses.map(response => {
        const processed = sortedByRemainder.find(item =>
            item.originalPercentage === response.percentage &&
            item.label === response.label
        );

        return {
            ...response,
            percentage: processed ? processed.floorValue : Math.round(response.percentage)
        };
    });
}

function AppDisplay() {
    const { chartData, isChartLoading, error } = useSurvey();

    // Track viewport for responsive label positioning
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 576 : false
    );
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 576);
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', handleResize);
        }
        return () => {
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        };
    }, []);

    // Mobile tooltips will be managed per-row to avoid re-rendering the list

    // if (chartData?.error) {
    //     return (
    //         <div className="app-display">
    //             <ErrorMessage message={chartData.error} />
    //         </div>
    //     );
    // }

    const sortedData = useMemo(() => {
        if (!chartData?.demographic_breakdown) return [];
        return chartData.demographic_breakdown.map(item => {
            const name = item.demographic_label;

            const rawResponses = Object.entries(item.response_distribution).map(([key, response]) => {
                return {
                    label: response.label,
                    percentage: response.percentage
                };
            });

            const roundedResponses = roundPercentagesToTotal(rawResponses);

            const responses = roundedResponses.map(response => ({
                label: response.label,
                value: response.percentage
            }));

            return { name, responses };
        });
    }, [chartData]);

    // Collect all unique labels across all demographic groups and determine their order
    const allLabels = useMemo(() => (
        [...new Set(sortedData.flatMap(row => row.responses.map(r => r.label)))]
    ), [sortedData]);

    // Sort labels based on their first appearance in the data
    // This ensures consistent ordering across all demographic groups
    const sortedLabels = useMemo(() => {
        const labelOrder = {};
        sortedData.forEach((row, rowIndex) => {
            row.responses.forEach((response, responseIndex) => {
                if (!(response.label in labelOrder)) {
                    labelOrder[response.label] = rowIndex * 1000 + responseIndex;
                }
            });
        });
        return [...allLabels].sort((a, b) => (labelOrder[a] || 0) - (labelOrder[b] || 0));
    }, [sortedData, allLabels]);

    const displayData = useMemo(() => {
        return sortedData.map(row => {
            const obj = { name: row.name };
            sortedLabels.forEach(label => {
                const response = row.responses.find(r => r.label === label);
                obj[label] = response ? response.value : 0;
            });
            return obj;
        });
    }, [sortedData, sortedLabels]);

    // Calculate dynamic height based on number of data points
    const perRowHeight = 70;
    const chartHeight = useMemo(() => displayData.length * perRowHeight, [displayData]);

    // Memoized mobile row chart to avoid re-rendering all rows on each tap
    const MobileRowChart = React.memo(function MobileRowChart({ row }) {
        const [isOpen, setIsOpen] = useState(false);

        useEffect(() => {
            const handler = (e) => {
                const activeName = e.detail;
                if (activeName !== row.name) {
                    setIsOpen(false);
                }
            };
            document.addEventListener('survey-mobile-tooltip-open', handler);
            return () => document.removeEventListener('survey-mobile-tooltip-open', handler);
        }, [row.name]);

        const handleActivate = () => {
            const next = !isOpen;
            setIsOpen(next);
            if (next) {
                document.dispatchEvent(new CustomEvent('survey-mobile-tooltip-open', { detail: row.name }));
            }
        };
        return (
            <div
                key={row.name}
                style={{ marginBottom: 16 }}
                onClick={handleActivate}
            >
                <div style={{
                    fontWeight: 'bold',
                    color: '#666',
                    margin: '0 0 0 4px',
                    lineHeight: '18px'
                }}>{row.name}</div>
                <div style={{ position: 'relative' }}>
                    {isOpen && (
                        <div style={{
                            position: 'absolute',
                            right: 8,
                            top: 4,
                            zIndex: 2,
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: 4,
                            padding: 0,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            overflow: 'hidden'
                        }}>
                            <p style={{
                                margin: 0,
                                fontWeight: 'bold',
                                backgroundColor: '#333',
                                color: 'white',
                                padding: '8px 10px',
                                borderBottom: '1px solid #ccc'
                            }}>{row.name}</p>
                            <div style={{ padding: '10px' }}>
                                {sortedLabels.map((label, index) => (
                                    <p key={label} style={{
                                        margin: '4px 0',
                                        color: COLORS[index % COLORS.length],
                                        fontWeight: 'bold'
                                    }}>
                                        {label}: {row[label]}%
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height={56}>
                        <BarChart
                            data={[row]}
                            layout="vertical"
                            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                            barCategoryGap={0}
                            barSize={36}
                        >
                            <XAxis type="number" hide domain={[0, 100]} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey="name" hide />
                            {sortedLabels.map((label, idx) => (
                                <Bar
                                    key={label}
                                    dataKey={label}
                                    stackId="a"
                                    fill={COLORS[idx % COLORS.length]}
                                    isAnimationActive={false}
                                    minPointSize={3}
                                >
                                    <LabelList dataKey={label} content={<CustomLabel />} />
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }, (prev, next) => prev.row.name === next.row.name);

    return (
        <div className="app-display">
            {error ? (
                <ErrorMessage message={error} />
            ) : isChartLoading || !chartData ? (
                <Loader />
            ) : (
                <>
                    {isMobile ? (
                        // Mobile: render each row as its own small chart with the label above
                        displayData.map((row) => (
                            <MobileRowChart key={row.name} row={row} />
                        ))
                    ) : (
                        // Desktop: original single chart with labels to the left
                        <ResponsiveContainer width="100%" height={chartHeight}>
                            <BarChart
                                data={displayData}
                                layout="vertical"
                                margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                                barCategoryGap={16}
                            >
                                <XAxis type="number" hide domain={[0, 100]} axisLine={false} tickLine={false} />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={150}
                                    tick={<CustomYAxisTick isMobile={false} />}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    wrapperStyle={{ zIndex: 9999, pointerEvents: 'none' }}
                                    formatter={(value) => `${value}%`}
                                    labelFormatter={(label) => label}
                                    content={({ active, payload, label }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <div className="custom-tooltip" style={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    padding: '0',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    overflow: 'hidden'
                                                }}>
                                                    <p style={{
                                                        margin: '0',
                                                        fontWeight: 'bold',
                                                        backgroundColor: '#333',
                                                        color: 'white',
                                                        padding: '8px 10px',
                                                        borderBottom: '1px solid #ccc'
                                                    }}>{label}</p>
                                                    <div style={{ padding: '10px' }}>
                                                        {payload
                                                            .sort((a, b) => {
                                                                const aIndex = sortedLabels.indexOf(a.dataKey);
                                                                const bIndex = sortedLabels.indexOf(b.dataKey);
                                                                return aIndex - bIndex;
                                                            })
                                                            .map((entry, index) => (
                                                                <p key={index} style={{
                                                                    margin: '4px 0',
                                                                    color: entry.color,
                                                                    fontWeight: 'bold'
                                                                }}>
                                                                    {entry.dataKey}: {entry.value}%
                                                                </p>
                                                            ))
                                                        }
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                {sortedLabels.map((label, idx) => (
                                    <Bar
                                        key={label}
                                        dataKey={label}
                                        stackId="a"
                                        fill={COLORS[idx % COLORS.length]}
                                        isAnimationActive={false}
                                        minPointSize={3}
                                    >
                                        <LabelList dataKey={label} content={<CustomLabel />} />
                                    </Bar>
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </>
            )}
        </div>
    );
}

export default AppDisplay;
